import {
  DEFAULT_PERMISSION_ROUTE_PRIORITY,
  ROUTE_PERMISSION_MATCHERS,
} from './catalog';

const DEFAULT_GRANTED_PERMISSIONS = [
  'providers.service_categories.view',
  'providers.service_categories.create',
  'providers.service_categories.update',
  'providers.service_categories.delete',
];

const RECOGNIZED_PERMISSION_NAMES = [
  'inventory.supplies.show',
  'work_orders.detail.show',
  'warehouse.warehouses.view',
  'warehouse.warehouses.show',
  'warehouse.warehouses.create',
  'warehouse.warehouses.update',
  'warehouse.warehouses.delete',
  'warehouse.stock.view',
  'warehouse.purchase_orders.view',
  'warehouse.purchase_orders.show',
  'warehouse.stock.update',
  'warehouse.entries.view',
  'warehouse.purchase_orders.create',
  'warehouse.purchase_orders.update',
  'warehouse.purchase_orders.change_status',
  'warehouse.purchase_orders.delete',
  'warehouse.entries.create',
  'warehouse.entries.export',
  'warehouse.exits.view',
  'warehouse.exits.create',
  'warehouse.movement_history.view',
  'warehouse.movement_history.export',
  'warehouse.transactions.view',
  'trucks.trucks.show',
  'trucks.documents.show',
  'providers.providers.view',
  'providers.providers.create',
  'providers.providers.show',
  'providers.providers.update',
  'providers.providers.delete',
  'providers.services.view',
  'providers.services.create',
  'providers.services.update',
  'providers.services.delete',
  'providers.service_categories.view',
  'providers.service_categories.create',
  'providers.service_categories.update',
  'providers.service_categories.delete',
  'users.users.show',
  'drivers.drivers.show',
  'specialists.specialists.show',
  'work_orders.detail.view',
];

export function isDefaultGrantedPermission(permissionName) {
  return DEFAULT_GRANTED_PERMISSIONS.includes(permissionName);
}

function hasRecognizedRoutePermission(permissions = []) {
  const routePermissions = DEFAULT_PERMISSION_ROUTE_PRIORITY.flatMap(
    (route) => {
      if (Array.isArray(route.anyOf)) {
        return route.anyOf;
      }

      if (route.permission) {
        return [route.permission];
      }

      return [];
    },
  ).filter(Boolean);

  const recognizedPermissions = new Set([
    ...routePermissions,
    ...RECOGNIZED_PERMISSION_NAMES,
  ]);

  return permissions.some((permission) => recognizedPermissions.has(permission));
}

export function normalizePermissionNames(rawPermissions = []) {
  if (!Array.isArray(rawPermissions)) {
    return [];
  }

  return rawPermissions
    .map((permission) => {
      if (typeof permission === 'string') {
        return permission;
      }

      if (permission && typeof permission.name === 'string') {
        return permission.name;
      }

      return null;
    })
    .filter(Boolean);
}

export function getStoredRoleNames() {
  const rawRole = localStorage.getItem('role');

  if (!rawRole) {
    return [];
  }

  try {
    const parsedRole = JSON.parse(rawRole);
    if (Array.isArray(parsedRole)) {
      return parsedRole.filter(Boolean);
    }

    if (parsedRole && typeof parsedRole === 'object') {
      return Object.values(parsedRole).filter(Boolean);
    }

    return [parsedRole].filter(Boolean);
  } catch (error) {
    return [rawRole].filter(Boolean);
  }
}

export function normalizeRoleNames(rawRole = []) {
  if (Array.isArray(rawRole)) {
    return rawRole.filter(Boolean);
  }

  if (rawRole && typeof rawRole === 'object') {
    return Object.values(rawRole).filter(Boolean);
  }

  return [rawRole].filter(Boolean);
}

export function isSuperAdmin(roleNames = getStoredRoleNames()) {
  return roleNames.includes('super-admin');
}

export function getStoredPermissions() {
  const rawPermissions = localStorage.getItem('permissions');

  if (!rawPermissions) {
    return [];
  }

  try {
    return normalizePermissionNames(JSON.parse(rawPermissions));
  } catch (error) {
    return [];
  }
}

export function hasStoredPermissionsPayload() {
  return localStorage.getItem('permissions') !== null;
}

export function hasStoredPermission(permissionName) {
  if (!permissionName) {
    return true;
  }

  if (isSuperAdmin()) {
    return true;
  }

  if (isDefaultGrantedPermission(permissionName)) {
    return true;
  }

  const permissions = getStoredPermissions();

  // Backward compatibility: only keep the permissive fallback when the app
  // has never received an explicit permissions payload in storage yet.
  if (!permissions.length && !hasStoredPermissionsPayload()) {
    return true;
  }

  if (!hasRecognizedRoutePermission(permissions)) {
    return true;
  }

  return permissions.includes(permissionName);
}

export function hasAnyStoredPermission(permissionNames = []) {
  if (!permissionNames.length) {
    return true;
  }

  if (isSuperAdmin()) {
    return true;
  }

  if (permissionNames.some((permissionName) => isDefaultGrantedPermission(permissionName))) {
    return true;
  }

  const currentPermissions = getStoredPermissions();

  if (!currentPermissions.length && !hasStoredPermissionsPayload()) {
    return true;
  }

  if (!hasRecognizedRoutePermission(currentPermissions)) {
    return true;
  }

  return permissionNames.some((permissionName) => currentPermissions.includes(permissionName));
}

function hasPermissionRule(rule = {}) {
  if (Array.isArray(rule.anyOf) && rule.anyOf.length) {
    return hasAnyStoredPermission(rule.anyOf);
  }

  if (rule.permission) {
    return hasStoredPermission(rule.permission);
  }

  return false;
}

export function getRequiredPermission(path) {
  const matcher = ROUTE_PERMISSION_MATCHERS.find(({ match }) => match.test(path));
  if (!matcher) {
    return null;
  }

  return matcher.permission || (matcher.anyOf ? matcher.anyOf[0] : null);
}

export function getRequiredPermissions(path) {
  const matcher = ROUTE_PERMISSION_MATCHERS.find(({ match }) => match.test(path));

  if (!matcher) {
    return [];
  }

  if (Array.isArray(matcher.anyOf)) {
    return matcher.anyOf;
  }

  if (matcher.permission) {
    return [matcher.permission];
  }

  return [];
}

export function getFirstAccessibleRoute() {
  const roleNames = getStoredRoleNames().map((roleName) => String(roleName).toLowerCase());
  const preferredRoutePaths = [];

  if (roleNames.includes('bodega')) {
    preferredRoutePaths.push('/warehouse');
  }

  if (roleNames.includes('operador') || roleNames.includes('operator')) {
    preferredRoutePaths.push('/ordenes-de-trabajo');
  }

  if (roleNames.includes('planificador')) {
    preferredRoutePaths.push('/ordenes-de-trabajo', '/historial-ordenes-de-trabajo');
  }

  if (roleNames.includes('administrador') || roleNames.includes('admin')) {
    preferredRoutePaths.push('/ordenes-de-trabajo', '/warehouse', '/users');
  }

  if (roleNames.includes('consulta')) {
    preferredRoutePaths.push('/reportes/expenses-by-truck', '/inventario');
  }

  const prioritizedRoutes = preferredRoutePaths.length
    ? DEFAULT_PERMISSION_ROUTE_PRIORITY.filter(
      (route) => preferredRoutePaths.includes(route.path),
    )
    : [];

  const firstPreferred = prioritizedRoutes.find((route) => hasPermissionRule(route));

  if (firstPreferred) {
    return firstPreferred.path;
  }

  const firstAccessible = DEFAULT_PERMISSION_ROUTE_PRIORITY.find(
    (rule) => hasPermissionRule(rule),
  );

  if (firstAccessible) {
    return firstAccessible.path;
  }

  const currentPermissions = getStoredPermissions();

  if (currentPermissions.length && !hasRecognizedRoutePermission(currentPermissions)) {
    return DEFAULT_PERMISSION_ROUTE_PRIORITY[0].path;
  }

  return '/login';
}
